import { useEffect, useState } from "react";
import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
// import dayjs from "dayjs";
import { useSnackbar } from "notistack";

import { Grid, Stack } from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import { updateStatusOrdered } from "src/api/purchaseOrder";

import font from "src/styles/font";
import device from "src/styles/device";
import { checkNotEmpty } from "src/common";
import { Black, DarkGrey, GreyStroke, Primary } from "src/styles/color";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import UploadPdf from "src/components/upload/dropzone/UploadPdf";
import PaymentComponent from "src/modules/order/purchase-order/modal/component/PaymentComponent";
import { renameFile } from "src/common/file";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    position: absolute;
    top: 16px;
    right: 16px;
    color: ${Black};
  }
`;

const WrapperBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WrapperTextField = styled(Stack)`
  width: 100%;
`;

const SpanTitle = styled.div`
  font-size: ${font.size.md};
  font-weight: 600;
  line-height: 24px;

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const TextFieldLabel = styled.p`
  margin: 0;
  font-size: ${font.size.sm};
  font-weight: 400;
  line-height: 24px;

  @media ${device.mobileL} {
    font-size: ${font.size.xs};
    line-height: 16px;
  }
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-adornedEnd {
    padding-right: 2px;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  .MuiOutlinedInput-root {
    padding-right: 12px;
  }
  &.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${DarkGrey};
  }
  .Mui-disabled,
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${GreyStroke};
  }

  /* @media ${device.mobileL} {
    text-align: left;
  } */
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": { borderRadius: 16 },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": { width: "100%", maxWidth: 640 },
  },
  "& .MuiDialogActions-root": { justifyContent: "center" },
  "& .MuiDialogActions-root>:not(:first-of-type)": { marginLeft: 24 },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": { margin: 24 },
    "& .MuiDialogContent-root": { padding: 16, paddingTop: 0 },
    "& .MuiDialogActions-root": { padding: 16 },
  },
}));

const QuotationDialog = ({ orderDetail, onClose, onSuccess, setIsLoading }) => {
  const { enqueueSnackbar } = useSnackbar();
  const defaultData = {
    qtNumber: "",
    scheduleDate: "",
    quotationPdf: "",
    // quotationOtherPdf: "",
  };
  const [updateOrder, setUpdateOrder] = useState(defaultData);
  // check is payment status === "deposit" before change to ordered
  const [isDeposit, setIsDeposit] = useState(false);

  // const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!orderDetail) return;

    let updateData = {
      ...defaultData,
      quotationPdf: orderDetail?.quotationPdf || "",
    };

    if (
      orderDetail?.statusPayment?.status === "deposit" &&
      orderDetail?.statusPaymentRePay?.status !== "paid"
    ) {
      updateData = {
        ...updateData,
        status: "paid",
        paySlipOtherImage: "",
        slipOtherDate: "",
      };
      setIsDeposit(true);
    }

    setUpdateOrder(updateData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail]);

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const updateParams = {
        ...updateOrder,
        ...(!!updateOrder?.quotationPdf?._id
          ? { quotationPdf: updateOrder?.quotationPdf?._id }
          : { quotationPdf: renameFile(updateOrder?.quotationPdf, "QTCS") }),
        ...(!!updateOrder?.quotationOtherPdf
          ? {
              quotationOtherPdf: renameFile(
                updateOrder?.quotationOtherPdf,
                "QTCS",
              ),
            }
          : {}),
        // ...(isDeposit
        //   ? {
        //       depositPrice: orderDetail?.totalVat,
        //     }
        //   : {}),
      };

      await updateStatusOrdered(orderDetail?._id, updateParams);
      enqueueSnackbar(`Update Quotation Success!`, { variant: "success" });
      await onSuccess();
    } catch (err) {
      console.error("err:", err.response);
      enqueueSnackbar(err.response.data.message, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const isPaymentValid = [
    "status",
    "paymentType",
    "paySlipOtherImage",
    "slipOtherDate",
  ].every((required) => {
    let objVal = updateOrder[required];
    return required === "slipOtherDate" ? objVal !== "" : checkNotEmpty(objVal);
  });

  const isQuoValid = ["qtNumber", "scheduleDate", "quotationPdf"].every(
    (required) => {
      let objVal = updateOrder[required];
      return required === "scheduleDate"
        ? objVal !== ""
        : checkNotEmpty(objVal);
    },
  );

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: { sm: 3, xs: 2 },
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: { sm: font.size.lg, xs: font.size.md },
          fontWeight: 600,
          span: { color: Primary, fontWeight: 400 },
        }}
      >
        {isDeposit ? (
          <>
            การชำระเงิน
            {!isPaymentValid && (
              <span>มียอดค้างชำระ กรุณาแนบหลักฐานการชำระเงิน</span>
            )}
          </>
        ) : (
          `QT Number`
        )}
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          {/* Payment Information */}
          {isDeposit && (
            <>
              <Grid item xs={12}>
                <PaymentComponent
                  isDeposit={isDeposit}
                  updateOrder={updateOrder}
                  setUpdateOrder={setUpdateOrder}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>
            </>
          )}

          {/* Quotation Number */}
          <Grid item xs={12}>
            <WrapperBox>
              {isDeposit && <SpanTitle>QT Number</SpanTitle>}

              <StyledTextField
                variant="outlined"
                fullWidth
                onChange={(e) => {
                  setUpdateOrder({ ...updateOrder, qtNumber: e.target.value });
                }}
                value={updateOrder.qtNumber}
                error={!checkNotEmpty(updateOrder.qtNumber)}
              />
            </WrapperBox>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Schedule Date */}
          <Grid item xs={12}>
            <WrapperBox>
              <SpanTitle>จัดส่งภายในวันที่</SpanTitle>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker
                  inputFormat="DD/MM/YYYY"
                  value={updateOrder?.scheduleDate}
                  onChange={(value) => {
                    setUpdateOrder({ ...updateOrder, scheduleDate: value.$d });
                  }}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      variant="outlined"
                      error={updateOrder?.scheduleDate === ""}
                    />
                  )}
                />
              </LocalizationProvider>
            </WrapperBox>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Attachment Quotation */}
          <Grid item xs={12}>
            <WrapperBox>
              <SpanTitle>เอกสาร</SpanTitle>

              <WrapperTextField gap={0.5}>
                <TextFieldLabel>ใบเสนอราคา (สำหรับลูกค้า)</TextFieldLabel>
                <StyledTextField
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <>
                        {checkNotEmpty(updateOrder?.quotationPdf) ? (
                          <IconButton
                            // sx={{ padding: 0 }}
                            onClick={() => {
                              setUpdateOrder({
                                ...updateOrder,
                                quotationPdf: "",
                              });
                            }}
                          >
                            <DeleteOutlineIcon
                              sx={{ color: Primary, cursor: "pointer" }}
                            />
                          </IconButton>
                        ) : (
                          <UploadPdf
                            callBackFile={(fileCallBlack) => {
                              setUpdateOrder({
                                ...updateOrder,
                                quotationPdf: fileCallBlack,
                              });
                            }}
                            closeAlert={false}
                          >
                            <IconButton>
                              <AttachFileIcon
                                sx={{
                                  color: Black,
                                  cursor: "pointer",
                                  fontSize: font.size.lg,
                                  ":hover": { color: "#e04132" },
                                }}
                              />
                            </IconButton>
                          </UploadPdf>
                        )}
                      </>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  value={
                    typeof updateOrder?.quotationPdf !== "object"
                      ? updateOrder?.quotationPdf
                      : updateOrder?.quotationPdf?.name ||
                        updateOrder?.quotationPdf?.originalFileName
                  }
                  error={!checkNotEmpty(updateOrder?.quotationPdf)}
                />
              </WrapperTextField>

              <WrapperTextField gap={0.5}>
                <TextFieldLabel>เอกสารเพิ่มเติม</TextFieldLabel>
                <StyledTextField
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <>
                        {checkNotEmpty(updateOrder?.quotationOtherPdf) ? (
                          <IconButton
                            // sx={{ padding: 0 }}
                            onClick={() => {
                              setUpdateOrder({
                                ...updateOrder,
                                quotationOtherPdf: "",
                              });
                            }}
                          >
                            <DeleteOutlineIcon
                              sx={{ color: Primary, cursor: "pointer" }}
                            />
                          </IconButton>
                        ) : (
                          <UploadPdf
                            callBackFile={(fileCallBlack) => {
                              setUpdateOrder({
                                ...updateOrder,
                                quotationOtherPdf: fileCallBlack,
                              });
                            }}
                            closeAlert={false}
                          >
                            <IconButton>
                              <AttachFileIcon
                                sx={{
                                  color: Black,
                                  cursor: "pointer",
                                  fontSize: font.size.lg,
                                  ":hover": { color: "#e04132" },
                                }}
                              />
                            </IconButton>
                          </UploadPdf>
                        )}
                      </>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  value={
                    typeof updateOrder?.quotationOtherPdf !== "object"
                      ? updateOrder?.quotationOtherPdf
                      : updateOrder?.quotationOtherPdf?.name || ""
                  }
                  // error={!checkNotEmpty(updateOrder?.quotationOtherPdf)}
                />
              </WrapperTextField>
            </WrapperBox>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => onClose()}
          fullWidth
          sx={{ maxWidth: 180 }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          disabled={!isQuoValid || (isDeposit ? !isPaymentValid : false)}
          onClick={() => onSubmit()}
          fullWidth
          sx={{ maxWidth: 180 }}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default QuotationDialog;
